import React, { ComponentProps, useEffect, useState } from "react";
import { connect } from "react-redux";

import { defineMessages, useIntl } from "react-intl";

import EmailTemplate from "@mapmycustomers/shared/types/email/EmailTemplate";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";
import { useNonNullableContext } from "@mapmycustomers/shared/util/react";
import { Button, useModal } from "@mapmycustomers/ui";

import { checkEmailSupporting, fetchEmailTemplates } from "@app/store/email/actions";

import EmailComposeModalsContext from "./creation/EmailComposeModalsContext";
import EmailTemplatesModal from "./EmailTemplatesModal";
import EmailsTemplatesModalContext, {
  EmailsTemplatesModalContextValue,
} from "./EmailTemplatesModalContext";

interface EmailTemplateModalProviderProps {
  children: React.ReactNode;
}

const messages = defineMessages({
  composeWithTemplateLabel: {
    id: "email.templatesModal.composeWithTemplateLabel",
    defaultMessage: "Compose with template",
    description: "Email templates modal compose with templates label",
  },
  openComposeButtonLabel: {
    id: "email.templatesModal.openComposeButtonLabel",
    defaultMessage: "Open Compose",
    description: "Email templates modal open compose button label",
  },
});

const EmailTemplateModalProvider = ({
  children,
  onEmailSupportingCheck,
  onEmailTemplatesFetch,
}: EmailTemplateModalProviderProps & typeof mapDispatchToProps) => {
  const intl = useIntl();
  const [modal, modalContextHolder] = useModal();
  const { onEmailModalShow } = useNonNullableContext(EmailComposeModalsContext);

  const [emailTemplatesModalOpen, onShowEmailTemplatesModal, onHideEmailTemplatesModal] =
    useBoolean();

  const [initialSelectedTemplateId, setInitialSelectedTemplateId] = useState<
    EmailTemplate["id"] | undefined
  >();

  const handleShowEmailTemplatesModal = useDynamicCallback<
    EmailsTemplatesModalContextValue["onShowEmailTemplatesModal"]
  >((selectedTemplateId) => {
    setInitialSelectedTemplateId(selectedTemplateId);
    onShowEmailTemplatesModal();
  });

  const handleHideEmailTemplatesModal = useDynamicCallback(() => {
    setInitialSelectedTemplateId(undefined);
    onHideEmailTemplatesModal();
  });

  const handleApply: ComponentProps<typeof EmailTemplatesModal>["onApply"] = useDynamicCallback(
    (templateInfo) => {
      handleHideEmailTemplatesModal();
      onEmailSupportingCheck({
        modal,
        successfulCallback: () => {
          onEmailModalShow({
            templateInfo,
          });
        },
      });
    }
  );

  const handleOpenComposeClick = useDynamicCallback(() => {
    handleHideEmailTemplatesModal();
    onEmailModalShow();
  });

  useEffect(() => {
    if (emailTemplatesModalOpen) {
      onEmailTemplatesFetch();
    }
  }, [emailTemplatesModalOpen, onEmailTemplatesFetch]);

  return (
    <EmailsTemplatesModalContext.Provider
      value={{
        emailTemplatesModalOpen,
        onHideEmailTemplatesModal: handleHideEmailTemplatesModal,
        onShowEmailTemplatesModal: handleShowEmailTemplatesModal,
      }}
    >
      {children}
      {emailTemplatesModalOpen && (
        <EmailTemplatesModal
          applyLabel={intl.formatMessage(messages.composeWithTemplateLabel)}
          initialSelectedTemplateId={initialSelectedTemplateId}
          noTemplatesActions={
            <Button onClick={handleOpenComposeClick} size="large" type="primary">
              {intl.formatMessage(messages.openComposeButtonLabel)}
            </Button>
          }
          onApply={handleApply}
          onHide={handleHideEmailTemplatesModal}
        />
      )}
      {modalContextHolder}
    </EmailsTemplatesModalContext.Provider>
  );
};

const mapDispatchToProps = {
  onEmailSupportingCheck: checkEmailSupporting,
  onEmailTemplatesFetch: fetchEmailTemplates.request,
};

export default connect(null, mapDispatchToProps)(EmailTemplateModalProvider);
